import { DateTime } from 'luxon';
import { utcToChicago } from '../../../utils/datetime/datetime-comprehensive';
import { isOngoingCourseItem } from './helpers';

// Memoized-in-the-hook summary/sort calculations for useDashboard. Extracted
// VERBATIM, parameterized on the relevant hook state + status functions
// instead of closing over them.

export const computeAssignmentSummary = (assignments, currentWeeklyAssignments, allActiveAssignments, getAssignmentStatus) => {
  try {
    // For display counts, use the filtered assignments (overdue/open)
    const allAssignmentsForDisplay = [...currentWeeklyAssignments, ...assignments];
    const overdue = allAssignmentsForDisplay.filter(assignment => {
      try {
        return getAssignmentStatus(assignment) === 'overdue';
      } catch (e) {
        console.error('Error getting assignment status for overdue:', e, assignment);
        return false;
      }
    }).length;
    const pending = allAssignmentsForDisplay.filter(assignment => {
      try {
        return getAssignmentStatus(assignment) === 'pending';
      } catch (e) {
        console.error('Error getting assignment status for pending:', e, assignment);
        return false;
      }
    }).length;
    const dueSoon = pending; // In the current setup, 'pending' effectively means 'due soon' if not overdue

    // For completed count, count ALL completed assignments from all active courses
    // This includes completed non-weekly assignments and all completed weeks of weekly assignments
    let completed = 0;

    if (Array.isArray(allActiveAssignments)) {
      allActiveAssignments.forEach(assignment => {
        if (!assignment) return; // Skip null/undefined assignments
        try {
          if (assignment.allow_weekly_submissions) {
            // For weekly assignments, count all completed weeks
            if (assignment.submitted_week_numbers) {
              let submittedWeeks = assignment.submitted_week_numbers;
              if (typeof submittedWeeks === 'string') {
                try {
                  submittedWeeks = JSON.parse(submittedWeeks);
                } catch (e) {
                  submittedWeeks = [];
                }
              }
              if (Array.isArray(submittedWeeks)) {
                // Count each completed week
                completed += submittedWeeks.length;
              }
            }
          } else {
            // For non-weekly assignments, count if submitted
            if (assignment.submitted_at) {
              completed += 1;
            }
          }
        } catch (e) {
          console.error('Error processing assignment for completed count:', e, assignment);
        }
      });
    }

    return { overdue, dueSoon, completed };
  } catch (error) {
    console.error('Error calculating assignment summary:', error);
    return { overdue: 0, dueSoon: 0, completed: 0 };
  }
};

export const computeRecentAssignments = (assignments, currentWeeklyAssignments, getAssignmentStatus) => {
  // Combine current weekly assignments with other assignments
  const allRelevantAssignments = [...currentWeeklyAssignments, ...assignments];

  // Filter to only unsubmitted assignments (or submitted for current weekly if it's overdue)
  const unsubmitted = allRelevantAssignments.filter(assignment => {
    if (assignment.allow_weekly_submissions && assignment.week_number) {
      // For weekly assignments, check if THIS specific week has been submitted
      let isThisWeekSubmitted = false;
      if (assignment.submitted_week_numbers) {
        let submittedWeeks = assignment.submitted_week_numbers;
        if (typeof submittedWeeks === 'string') {
          try {
            submittedWeeks = JSON.parse(submittedWeeks);
          } catch (e) {
            submittedWeeks = [];
          }
        }
        if (Array.isArray(submittedWeeks)) {
          isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === assignment.week_number);
        }
      }
      // Fallback to submitted_at if submitted_week_numbers not available
      if (!isThisWeekSubmitted && assignment.submitted_at) {
        isThisWeekSubmitted = true;
      }
      // Show if not submitted for this week, or if submitted but marked as late
      return !isThisWeekSubmitted || getAssignmentStatus(assignment) === 'late';
    }
    return !assignment.submitted_at;
  });

  return unsubmitted
    .sort((a, b) => {
      const statusA = getAssignmentStatus(a);
      const statusB = getAssignmentStatus(b);

      // Prioritize current weekly assignments
      if (a.allow_weekly_submissions && a.week_number && !b.allow_weekly_submissions) return -1;
      if (b.allow_weekly_submissions && b.week_number && !a.allow_weekly_submissions) return 1;

      // Overdue first
      if (statusA === 'overdue' && statusB !== 'overdue') return -1;
      if (statusB === 'overdue' && statusA !== 'overdue') return 1;

      // Then sort by due date (soonest first)
      let dateA, dateB;
      try {
        if (a.display_due_date && DateTime.isDateTime(a.display_due_date)) {
          dateA = a.display_due_date;
        } else if (a.due_date) {
          dateA = utcToChicago(a.due_date);
        } else {
          dateA = DateTime.fromISO('2099-12-31');
        }
      } catch (e) {
        dateA = DateTime.fromISO('2099-12-31');
      }

      try {
        if (b.display_due_date && DateTime.isDateTime(b.display_due_date)) {
          dateB = b.display_due_date;
        } else if (b.due_date) {
          dateB = utcToChicago(b.due_date);
        } else {
          dateB = DateTime.fromISO('2099-12-31');
        }
      } catch (e) {
        dateB = DateTime.fromISO('2099-12-31');
      }

      // If both overdue or both pending, sort by date
      if (statusA === statusB) {
        return dateA.toMillis() - dateB.toMillis();
      }

      // Overdue should come before pending
      if (statusA === 'overdue') return -1;
      if (statusB === 'overdue') return 1;

      return dateA - dateB;
    });
};

export const computeQuizSummary = (quizzes, getQuizStatus) => {
  // Filter only quizzes from currently ongoing courses.
  const activeQuizzes = quizzes.filter(isOngoingCourseItem);
  const completed = activeQuizzes.filter(q => q.status === 'completed').length;
  const overdue = activeQuizzes.filter(q => {
    const status = getQuizStatus(q);
    return status === 'overdue';
  }).length;
  const open = activeQuizzes.filter(q => {
    const status = getQuizStatus(q);
    return status === 'pending' || status === 'in-progress';
  }).length;

  return { open, overdue, completed };
};

export const computeRecentQuizzes = (quizzes) => {
  // Filter only quizzes from currently ongoing courses.
  const activeQuizzes = quizzes.filter(isOngoingCourseItem);
  return activeQuizzes
    .filter(quiz => quiz.status === 'not_started' || quiz.status === 'in_progress') // Only show not started or in progress quizzes
    .sort((a, b) => {
      const dateA = a.assignment_due_at || a.quiz_due_at ? new Date(a.assignment_due_at || a.quiz_due_at) : new Date('2099-12-31');
      const dateB = b.assignment_due_at || b.quiz_due_at ? new Date(b.assignment_due_at || b.quiz_due_at) : new Date('2099-12-31');
      return dateA - dateB; // Soonest due first
    });
};

export const computeNotesSummary = (notes) => {
  const total = notes.length;
  const pinned = notes.filter(n => n.is_pinned).length;
  const recent = notes.filter(n => {
    const noteDate = new Date(n.updated_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return noteDate > weekAgo;
  }).length;

  return { total, pinned, recent };
};

export const computeRecentNotes = (notes) => {
  return notes
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 3);
};
